import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { mergeCollectionAssetVision } from "actions/mediaLibrary";
import {
  analyzeAssetVision,
  getAssetVision,
  type AssetVision,
} from "api/media-library/vision";
import type { CollectionFile } from "shared/types/files";
import { trackEventFx } from "libs/analytics";
import { LIBRARY_VISION_ANALYSIS } from "constants/Events/MediaLibraryTrackEvents";

export function useAssetVision(asset: CollectionFile) {
  const dispatch = useDispatch();
  const assetRef = useRef(asset);
  assetRef.current = asset;
  const request = useRef<AbortController | null>(null);
  const [vision, setVision] = useState<AssetVision>();
  const [pending, setPending] = useState<"load" | "analyze" | null>(null);
  const [error, setError] = useState(false);
  const { asset_id: assetId, _id: inboxEntryId } = asset;

  const run = useCallback(
    async (analyze: boolean) => {
      request.current?.abort();
      const controller = new AbortController();
      request.current = controller;
      const requestedAsset = assetRef.current;
      setPending(analyze ? "analyze" : "load");
      setError(false);
      try {
        const result = await (analyze ? analyzeAssetVision : getAssetVision)(
          assetId,
          controller.signal
        );
        if (controller.signal.aborted) return;
        if (
          result.assetId !== assetId ||
          result.inboxEntryId !== inboxEntryId
        ) {
          throw new Error("Vision response does not match the open asset");
        }
        setVision(result);
        dispatch(
          mergeCollectionAssetVision({
            asset: requestedAsset,
            vision: result,
            isReadResponse: !analyze,
          })
        );
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (!controller.signal.aborted) setPending(null);
      }
    },
    [assetId, inboxEntryId, dispatch]
  );

  useEffect(() => {
    void run(false);
    return () => request.current?.abort();
  }, [run]);

  const analyze = () => {
    if (
      pending ||
      !vision?.canAnalyze ||
      asset.visionDescription ||
      vision.status === "complete" ||
      vision.status === "skipped-too-long"
    )
      return;
    if (vision.status === "failed" || error) {
      void trackEventFx({
        eventName: LIBRARY_VISION_ANALYSIS,
        metadata: {
          action: "retry-click",
          asset_id: assetId,
          inbox_entry_id: inboxEntryId,
          collection_id: asset.inbox_id,
        },
      }).catch(() => {});
    }
    void run(true);
  };

  return { vision, pending, error, analyze, reload: () => void run(false) };
}
